/**
 * "Buy first" on the batch pick list: the just-in-time blanks this batch needs
 * bought before it is picked, one block per just-in-time PO (PO number,
 * supplier, lines). Production App surface (PRODUCTION_DESIGN.md): large type,
 * semantic colour for state, 48px+ targets.
 *
 * Four components, one data shape (`JitBuyList`, no prices: pickers read it);
 * the plain helpers (anchor, print CSS, content rule) are in `utils/buy-first`:
 *   - `BuyFirstPrintSection`: the printed twin, INLINE-STYLED. usePrintLabels
 *     copies the sheet's innerHTML into a bare iframe with no Tailwind, so
 *     nothing here may lean on a class for its look. With `breakBefore` it
 *     always starts a new sheet (the inline style, plus `BUY_FIRST_PRINT_CSS`
 *     in the pick-list print for engines that read only the stylesheet).
 *   - `BuyFirstCard`: the on-screen card (Tailwind), the same content plus a
 *     link per PO and "Update buy list". The link shows only when the PO is
 *     the caller's to open (`buyList.canOpenPo`: a client at a shared floor
 *     reads the list, but the PO sits in the floor owner's org).
 *   - `BuyFirstMenu`: the three-dots menu in the button row with "Print buy
 *     list". It sits outside every print template, so it never prints.
 *
 * BLU-1506: blanks that already arrived on an Order and wait PO come after the
 * POs, one block per box: "From {supplier} box {poNumber}, not the shelf" and
 * its lines (`buyList.fromBoxes`, no prices). With nothing to buy, the card
 * and the sheet take the box title instead of "Buy first".
 *
 * KEEP-IN-LOCKSTEP: the card and the print section show the same rows in the
 * same order with the same words (the BatchDetailScreen picklist twins rule).
 */
import { useEffect, useId, useRef } from 'react';
import { Link, useLocation as useRouterLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { JitBuyList, JitBuyListFromBox, JitBuyListLine, JitBuyListPurchaseOrder } from '@threadheads/shared';
import {
  AlertTriangle,
  Check,
  Clock,
  ExternalLink,
  Loader2,
  MoreHorizontal,
  PackageOpen,
  Printer,
  RefreshCw,
  ShoppingCart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { blankStyleName } from '@/lib/blank-style-name';
import { cn } from '@/lib/utils';
import {
  BUY_FIRST_ANCHOR,
  buyListBlankCount,
  fromBoxesBlankCount,
  fromBoxesOf,
  hasBlanksToBuy,
} from '../utils/buy-first';

const DASH = '-';
/**
 * The Item column: the style alone. Colour and Size have their own columns,
 * and a blank's SKU title already ends in both ("Staple Tee M Black").
 */
const itemName = (line: JitBuyListLine): string =>
  (line.title?.trim() ? blankStyleName(line.title, line.colour, line.size) : null) ?? line.sku ?? DASH;
const lineKey = (groupId: string, line: JitBuyListLine, index: number) =>
  `${groupId}:${line.skuId ?? line.sku ?? 'line'}:${index}`;
const supplierLabel = (t: TFunction, po: JitBuyListPurchaseOrder): string =>
  po.supplierName ?? t('batch.buyFirst.noSupplier');
/** "From {supplier} box {number}, not the shelf": one sentence, the same on screen and paper. */
const boxHeading = (t: TFunction, box: JitBuyListFromBox): string =>
  box.supplierName
    ? t('batch.fromBoxes.heading', { supplier: box.supplierName, number: box.poNumber })
    : t('batch.fromBoxes.headingNoSupplier', { number: box.poNumber });

// ── Print (inline styles only) ───────────────────────────────────────────────

const cellStyle = { border: '1px solid #000', padding: '8px 10px', textAlign: 'left' as const };
const headStyle = { ...cellStyle, backgroundColor: '#f0f0f0' };

/** One PO's or one box's lines on paper: Got (tick box), Item, Colour, Size, SKU, Qty. */
function PrintLinesTable({ groupId, lines }: { groupId: string; lines: JitBuyListLine[] }) {
  const { t } = useTranslation('production');
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ ...headStyle, textAlign: 'center', width: '50px' }}>{t('batch.buyFirst.colGot')}</th>
          <th style={headStyle}>{t('batch.buyFirst.colItem')}</th>
          <th style={headStyle}>{t('batch.buyFirst.colColour')}</th>
          <th style={headStyle}>{t('batch.buyFirst.colSize')}</th>
          <th style={headStyle}>{t('batch.buyFirst.colSku')}</th>
          <th style={{ ...headStyle, textAlign: 'right', width: '60px' }}>{t('batch.buyFirst.colQty')}</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => (
          <tr key={lineKey(groupId, line, index)}>
            <td style={{ ...cellStyle, textAlign: 'center' }}>
              <span style={{ fontSize: '18px' }}>&#9744;</span>
            </td>
            <td style={cellStyle}>{itemName(line)}</td>
            <td style={cellStyle}>{line.colour ?? DASH}</td>
            <td style={cellStyle}>{line.size ?? DASH}</td>
            <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{line.sku ?? DASH}</td>
            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700 }}>{line.qty}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface BuyFirstPrintSectionProps {
  buyList: JitBuyList;
  batchNumber: number | string;
  /** Start a new sheet: true at the bottom of the pick list, false when printed alone. */
  breakBefore?: boolean;
}

export function BuyFirstPrintSection({ buyList, batchNumber, breakBefore = false }: BuyFirstPrintSectionProps) {
  const { t } = useTranslation('production');
  const buying = hasBlanksToBuy(buyList);
  const boxes = fromBoxesOf(buyList);
  return (
    <div
      className={breakBefore ? 'buy-first' : undefined}
      data-testid="buy-first-print"
      style={breakBefore ? { breakBefore: 'page', pageBreakBefore: 'always' } : undefined}
    >
      <h2 style={{ fontSize: '22px', marginBottom: '6px' }}>
        {buying
          ? t('batch.buyFirst.printTitle', { number: batchNumber })
          : t('batch.fromBoxes.printTitle', { number: batchNumber })}
      </h2>
      <p style={{ marginBottom: '16px', color: '#333' }}>
        {buying ? t('batch.buyFirst.intro') : t('batch.fromBoxes.intro')}
      </p>

      {buyList.unlinkedUnits > 0 && (
        // Text inside a border, never colour alone: the sheet is often printed in black and white.
        <p style={{ border: '2px solid #000', padding: '8px 10px', marginBottom: '16px', fontWeight: 700 }}>
          {t('batch.buyFirst.unlinkedPrint', { count: buyList.unlinkedUnits })}
        </p>
      )}

      {buyList.purchaseOrders.map((po) => (
        <div key={po.poId} style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '17px', marginBottom: '8px' }}>
            {/* The number alone: it already reads as a PO ("PO-AU-001"). */}
            <span style={{ fontFamily: 'monospace' }}>{po.poNumber}</span>
            {' · '}
            {supplierLabel(t, po)}
          </h3>
          <PrintLinesTable groupId={po.poId} lines={po.lines} />
          <p style={{ marginTop: '6px', fontSize: '12px', color: '#333' }}>
            {t('batch.buyFirst.recordOn', { number: po.poNumber })}
          </p>
        </div>
      ))}

      {/* BLU-1506: blanks that already arrived for these orders, after the POs. */}
      {boxes.map((box) => (
        <div key={`box-${box.poId}`} style={{ marginBottom: '24px' }} data-testid="buy-first-box-print">
          <h3 style={{ fontSize: '17px', marginBottom: '8px' }}>{boxHeading(t, box)}</h3>
          <PrintLinesTable groupId={`box-${box.poId}`} lines={box.lines} />
        </div>
      ))}
    </div>
  );
}

// ── Screen ───────────────────────────────────────────────────────────────────

/** One PO's or one box's lines on screen: Item, Colour, Size, SKU, Qty. */
function ScreenLinesTable({ groupId, lines }: { groupId: string; lines: JitBuyListLine[] }) {
  const { t } = useTranslation('production');
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[560px]">
        <thead className="bg-muted">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-sm font-medium">{t('batch.buyFirst.colItem')}</th>
            <th scope="col" className="px-4 py-3 text-left text-sm font-medium">{t('batch.buyFirst.colColour')}</th>
            <th scope="col" className="px-4 py-3 text-left text-sm font-medium">{t('batch.buyFirst.colSize')}</th>
            <th scope="col" className="px-4 py-3 text-left text-sm font-medium">{t('batch.buyFirst.colSku')}</th>
            <th scope="col" className="px-4 py-3 text-right text-sm font-medium">{t('batch.buyFirst.colQty')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={lineKey(groupId, line, index)} className={index % 2 === 0 ? 'bg-card' : 'bg-muted/30'}>
              <td className="px-4 py-3 text-base">{itemName(line)}</td>
              <td className="px-4 py-3 text-base">{line.colour ?? DASH}</td>
              <td className="px-4 py-3 text-lg font-bold">{line.size ?? DASH}</td>
              <td className="min-w-0 break-all px-4 py-3 font-mono text-base">{line.sku ?? DASH}</td>
              <td className="px-4 py-3 text-right text-lg font-bold tabular-nums">{line.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PoStatusChip({ status }: { status: string }) {
  const { t } = useTranslation('production');
  if (status === 'received') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800">
        <Check className="h-4 w-4" aria-hidden="true" />
        {t('batch.buyFirst.status.bought')}
      </span>
    );
  }
  if (status === 'submitted') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-800">
        <Clock className="h-4 w-4" aria-hidden="true" />
        {t('batch.buyFirst.status.toBuy')}
      </span>
    );
  }
  return null;
}

interface BuyFirstCardProps {
  buyList: JitBuyList;
  /** Coordinator tier (the syncBuyList gate): shows "Update buy list". */
  canUpdate: boolean;
  /**
   * `purchase_orders.view`: links each PO to its page. Pickers get no link,
   * and neither does anyone when `buyList.canOpenPo` is false (the POs sit in
   * the floor owner's org, so the link would land on a page they cannot read).
   */
  canOpenPurchaseOrders: boolean;
  onUpdate: () => void;
  isUpdating: boolean;
}

export function BuyFirstCard({ buyList, canUpdate, canOpenPurchaseOrders, onUpdate, isUpdating }: BuyFirstCardProps) {
  const { t } = useTranslation('production');
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const { hash } = useRouterLocation();
  const buying = hasBlanksToBuy(buyList);
  const boxes = fromBoxesOf(buyList);
  // With something to buy the count is what to buy; otherwise what to take from the boxes.
  const blanks = buying ? buyListBlankCount(buyList) : fromBoxesBlankCount(buyList);
  const showOpenPo = canOpenPurchaseOrders && buyList.canOpenPo;

  // The createBatch toast's "Open batch" lands here: bring the card into view
  // once, without animation (an operator never watches a scroll).
  useEffect(() => {
    if (hash === `#${BUY_FIRST_ANCHOR}`) cardRef.current?.scrollIntoView?.({ block: 'start' });
  }, [hash]);

  return (
    <Card ref={cardRef} id={BUY_FIRST_ANCHOR} aria-labelledby={titleId} data-testid="buy-first-card" className="scroll-mt-4">
      <CardHeader className="space-y-2 py-4">
        <CardTitle id={titleId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-2">
            {buying ? (
              <ShoppingCart className="h-5 w-5" aria-hidden="true" />
            ) : (
              <PackageOpen className="h-5 w-5" aria-hidden="true" />
            )}
            {buying ? t('batch.buyFirst.title') : t('batch.fromBoxes.title')}
          </span>
          {blanks > 0 && (
            <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-sm font-semibold tabular-nums text-sky-800">
              {t('batch.buyFirst.blankCount', { count: blanks })}
            </span>
          )}
        </CardTitle>
        <p className="text-base text-muted-foreground">
          {buying ? t('batch.buyFirst.intro') : t('batch.fromBoxes.intro')}
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {buyList.unlinkedUnits > 0 && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
            data-testid="buy-first-unlinked"
          >
            <p className="flex min-w-0 flex-1 basis-64 items-start gap-2 text-base text-amber-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
              <span>
                {t('batch.buyFirst.unlinked', { count: buyList.unlinkedUnits })}{' '}
                {!canUpdate && t('batch.buyFirst.askCoordinator')}
              </span>
            </p>
            {canUpdate && (
              <Button size="lg" className="h-12 gap-2" onClick={onUpdate} disabled={isUpdating}>
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                ) : (
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                )}
                {isUpdating ? t('batch.buyFirst.updating') : t('batch.buyFirst.update')}
              </Button>
            )}
          </div>
        )}

        {buyList.purchaseOrders.map((po) => {
          const headingId = `${titleId}-${po.poId}`;
          return (
            <section key={po.poId} aria-labelledby={headingId} className="space-y-3" data-testid="buy-first-po">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <h3 id={headingId} className="min-w-0 text-lg font-semibold [overflow-wrap:anywhere]">
                  <span className="font-mono tabular-nums">{po.poNumber}</span>
                  <span className="text-muted-foreground" aria-hidden="true">
                    {' · '}
                  </span>
                  <span className={cn(po.supplierName === null && 'text-amber-800')}>{supplierLabel(t, po)}</span>
                </h3>
                <PoStatusChip status={po.status} />
                {showOpenPo && (
                  <Button asChild variant="outline" size="lg" className="ml-auto h-12 gap-2">
                    <Link to={`/orders/${po.poId}`} aria-label={t('batch.buyFirst.openPoAria', { number: po.poNumber })}>
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                      {t('batch.buyFirst.openPo')}
                    </Link>
                  </Button>
                )}
              </div>

              <ScreenLinesTable groupId={po.poId} lines={po.lines} />

              {po.status !== 'received' && (
                <p className="text-base text-muted-foreground">{t('batch.buyFirst.recordOn', { number: po.poNumber })}</p>
              )}
            </section>
          );
        })}

        {/* BLU-1506: blanks that already arrived for these orders, after the POs. */}
        {boxes.map((box) => {
          const headingId = `${titleId}-box-${box.poId}`;
          return (
            <section key={`box-${box.poId}`} aria-labelledby={headingId} className="space-y-3" data-testid="buy-first-box">
              <h3 id={headingId} className="flex min-w-0 items-start gap-2 text-lg font-semibold [overflow-wrap:anywhere]">
                <PackageOpen className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span>{boxHeading(t, box)}</span>
              </h3>
              <ScreenLinesTable groupId={`box-${box.poId}`} lines={box.lines} />
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * The buy list could not be read. Said out loud rather than hidden: a batch
 * that needs blanks bought would otherwise look like one that does not.
 * Its own block (not SectionError) for the 48px Retry a station needs.
 */
export function BuyFirstLoadError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  const { t } = useTranslation('production');
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
      data-testid="buy-first-error"
    >
      <div className="flex min-w-0 flex-1 basis-64 items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
        <div className="min-w-0 space-y-0.5">
          <p className="text-base font-semibold">{t('batch.buyFirst.loadError')}</p>
          <p className="text-base text-muted-foreground">{t('batch.buyFirst.loadErrorBody')}</p>
        </div>
      </div>
      <Button variant="outline" size="lg" className="h-12 gap-2" onClick={onRetry} disabled={retrying}>
        {retrying ? (
          <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        ) : (
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        )}
        {t('batch.buyFirst.retry')}
      </Button>
    </div>
  );
}

interface BuyFirstMenuProps {
  batchNumber: number | string;
  onPrintBuyList: () => void;
  isPrinting: boolean;
}

/** The button row's three-dots menu. Rendered only when the batch has a just-in-time PO. */
export function BuyFirstMenu({ batchNumber, onPrintBuyList, isPrinting }: BuyFirstMenuProps) {
  const { t } = useTranslation('production');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          className="h-14 w-14 shrink-0 self-start p-0 print:hidden sm:self-auto"
          aria-label={t('batch.buyFirst.moreActions', { number: batchNumber })}
          data-testid="batch-more-actions"
        >
          {isPrinting ? (
            <Loader2 className="h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="print:hidden">
        <DropdownMenuItem className="min-h-12 gap-2 text-base" disabled={isPrinting} onSelect={onPrintBuyList}>
          <Printer className="h-4 w-4" aria-hidden="true" />
          {t('batch.buyFirst.printBuyList')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
